import {Stack} from 'react-bootstrap';
import {useRef} from 'react';
import styled from 'styled-components';
import {useIsMobile} from 'utils/general';
import {SunshineLoadingAnimation} from './SunshineLoadingAnimation';

const DESKTOP_LOADING_ANIMATION_SCALE = 0.6;
const MOBILE_LOADING_ANIMATION_SCALE = 0.4;

/** Encouraging messages shown under the sun; one is picked at random per mount. */
const SUNSHINE_QUOTES = [
  'You are doing better than you think.',
  'Every bug you fix makes you stronger.',
  'Small steps still move you forward.',
  'Mistakes just mean you are learning.',
  'You are capable of amazing things.',
  'Keep going — brighter things are coming.',
  'Progress over perfection, always.',
  'Great things take a little loading time.',
];

// Coloured to match the sun's burnt-orange outline in the animation.
const SunshineQuote = styled.p`
  margin-top: 0.75rem;
  font-size: 1.05rem;
  font-style: italic;
  color: #c1742d;
  text-align: center;
`;

/**
 * Picks a random encouraging message for the cipx loader.
 *
 * @returns One of {@link SUNSHINE_QUOTES}, chosen uniformly at random.
 * @throws Never.
 */
function getRandomSunshineQuote(): string {
  const index = Math.floor(Math.random() * SUNSHINE_QUOTES.length);
  return SUNSHINE_QUOTES[index];
}

/**
 * The cipx standard-loading view: an animated sun that draws on a smiley face,
 * with a random encouraging message below it. Wired in as the per-course
 * `LoadingPage` override for cipx (see `index.tsx` and
 * {@link CourseAppOverrides}), replacing the default Karel loader — Karel is
 * off-brand for cipx, which doesn't teach it.
 *
 * @returns The rendered cipx loading view.
 * @throws Never.
 */
export function CipxLoadingPage() {
  const isMobile = useIsMobile();
  // eslint-disable-next-line @eslint-react/naming-convention/ref-name -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const sunshineQuote = useRef(getRandomSunshineQuote());
  const scale = isMobile
    ? MOBILE_LOADING_ANIMATION_SCALE
    : DESKTOP_LOADING_ANIMATION_SCALE;

  return (
    <Stack className="align-items-center justify-content-center">
      <SunshineLoadingAnimation scale={scale} />
      <SunshineQuote>{sunshineQuote.current}</SunshineQuote>
    </Stack>
  );
}
